import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import api from "/imports/api/client";
import { NO_CAPTURE } from "/imports/libs/analytics";

const MAX_NOTE_LENGTH = 1000;

function BookingNoteDialog({ booking, onClose, onSaved }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const open = Boolean(booking);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const existing = booking?.trainerNote?.text || "";

  useEffect(() => {
    if (!booking) return;
    setText(existing);
  }, [booking]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > MAX_NOTE_LENGTH;
  const clearing = !trimmed && Boolean(existing);
  const canSave = !tooLong && trimmed !== existing;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await api.saveTrainerBookingNote({ bookishId: booking._id, text: trimmed });
      onSaved?.(booking._id, res.note ?? null);
      const msg = clearing ? "Note removed" : existing ? "Note updated" : "Note added";
      enqueueSnackbar(msg, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "center" } });
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || "Couldn't save your note", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "center" } });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog className={NO_CAPTURE} open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{existing ? "Edit your note" : "Add a note"}</DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {clearing ? "Saving now will remove your note from this booking." : "Your note is shared with the club's admins."}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          value={text}
          disabled={saving}
          onChange={(e) => setText(e.target.value)}
          placeholder="How did the session go?"
          error={tooLong}
          helperText={tooLong ? `${trimmed.length} / ${MAX_NOTE_LENGTH} characters` : `${trimmed.length} / ${MAX_NOTE_LENGTH}`}
        />
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
        <Button color="inherit" onClick={onClose} disabled={saving} sx={{ color: "text.secondary" }}>
          Cancel
        </Button>
        <Box sx={{ position: "relative" }}>
          <Button
            variant="contained"
            color={clearing ? "error" : "primary"}
            onClick={handleSave}
            disabled={!canSave || saving}
            sx={{ minWidth: "7.5rem" }}
          >
            {clearing ? "Remove" : "Save"}
          </Button>
          {saving && <CircularProgress size={18} sx={{ position: "absolute", top: "50%", left: "50%", mt: "-9px", ml: "-9px" }} />}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default BookingNoteDialog;
